import React, { useContext, useState } from 'react';
import variables from '../../../assets/styles/variables.module.scss';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    Button,
    Box,
    IconButton,
    styled,
    FormControl,
    DialogActions,
    Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import QRCode from "react-qr-code";
import ConfirmBase from "../../../Shared-ui/Types/Confirm";
import ConfirmPopup from "../../../Shared-ui/SharedComponents/ConfirmPopup";
import { useDispatch } from 'react-redux';
import BaseInstance from '../../../Services/AxiosInstance/BaseInstance';
import { StartLoading, StopLoading } from '../../../Shared-ui/Store/LoadingReducer/LoaderActions';
import { ShowError, ShowSuccess } from '../../../Shared-ui/Store/MessageReducer/MsgActions';
import { MFA_DISABLE, MFA_ENABLE, MFA_SETUP } from '../../../Services/Endpoint';
import { GetSession } from '../../../Shared-ui/utils/Auth/AuthHelpers';
import userContext from '../../../Shared-ui/Context/UserContext';

interface MyComponentProps {
    enableOrDisableMFA: string
    showMFAForm: boolean;
    setShowMFAForm: (value: boolean) => void;
    qrCodeData: { qr_code: string, secret: string }
}


const VerificationBox = styled(Box)({
    "& .MuiOutlinedInput-root": {
        "& fieldset": {
            borderColor: "#e0e0e0",
        },
        "&:hover fieldset": {
            borderColor: "#80bdff",
        },
        "&.Mui-focused fieldset": {
            borderColor: "#1976d2",
        },
    },
    "& input": {
        textAlign: "center",
        letterSpacing: "0.5em",
        fontSize: "1.5rem",
    },
});

const MFAPopup: React.FC<MyComponentProps> = (props) => {
    const [open, setOpen] = useState(false);
    const [qrValue, setQrValue] = useState('https://example.com');
    const [otpValues, setOtpValues] = useState<string[]>(Array(6).fill(""));
    const handleOpen = () => setOpen(true);
    const [verificationCode, setVerificationCode] = useState<string>("");
    const [confirmPopupData, setConfirmPopupData] = useState<ConfirmBase>({ show: false })
    const { user, updateUserContext } = useContext(userContext);
    const dispatch = useDispatch();


    const handleClose = () => {
        props.setShowMFAForm(false);
        updateUserContext()
    }

    const handleOtpChange = (index: number, value: string) => {
        if (!/^[0-9]*$/.test(value)) return;

        const newOtpValues = [...otpValues];
        newOtpValues[index] = value;
        setOtpValues(newOtpValues);
        setVerificationCode(newOtpValues.join(""));

        // Auto focus next input
        if (value && index < 5) {
            const nextInput = document.getElementById(`otp-${index + 1}`);
            nextInput?.focus();
        }
    };

    const handleKeyDown = (
        index: number,
        e: React.KeyboardEvent<HTMLInputElement | HTMLDivElement>
    ) => {
        if (e.key === "Backspace" && !otpValues[index] && index > 0) {
            const prevInput = document.getElementById(
                `otp-${index - 1}`
            ) as HTMLInputElement;
            prevInput?.focus();
        }
    };


    const disableMFA = async () => {
        try {
            setConfirmPopupData((preVal) => ({ ...preVal, show: false }));
            dispatch(StartLoading());
            const data = await BaseInstance.post(MFA_DISABLE, { otp: verificationCode, temp_token: GetSession() }).then(
                (res) => res?.data
            );
            dispatch(ShowSuccess(data.message));
            handleClose();
        } catch (err: any) {
            dispatch(ShowError(err?.response?.data?.detail));
        } finally {
            dispatch(StopLoading());
        }

    }

    const enableMFA = async () => {
        try {
            dispatch(StartLoading());
            const data = await BaseInstance.post(MFA_ENABLE, { otp: verificationCode }).then(
                (res) => res?.data
            );
            dispatch(ShowSuccess(data.message));
            handleClose();
        } catch (err: any) {
            dispatch(ShowError(err?.response?.data?.detail));
        } finally {
            dispatch(StopLoading());
        }

    }



    return (
        <>
            <Dialog open={props.showMFAForm} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {props.enableOrDisableMFA === "enable" ? "QR Code Verification" : "OTP Code Verification"}
                    <IconButton
                        aria-label="close"
                        onClick={handleClose}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent>
                    <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        gap={2}
                        mt={2}
                    >
                        {props.enableOrDisableMFA === "enable" && (
                            <>
                                <Box sx={{ mb: 2 }}>
                                    <span style={{ fontSize: '1rem', color: variables.textColor }}>
                                        Use an authenticator app like Google or Microsoft Authenticator to scan this QR code
                                    </span>
                                </Box>
                                <Box sx={{ background: variables.whiteColor, p: 2, borderRadius: 2 }}>
                                    <img
                                        src={`data:image/png;base64,${props.qrCodeData.qr_code}`}
                                        alt="QR Code"
                                        width={200}
                                        height={200}
                                    />
                                </Box>

                            </>
                        )}

                        <VerificationBox>
                            <FormControl fullWidth variant="outlined">
                                <Box
                                    sx={{
                                        display: "flex",
                                        gap: 1,
                                        mt: 1,
                                        justifyContent: "space-between",
                                    }}
                                >
                                    {Array(6)
                                        .fill(0)
                                        .map((_, index) => (
                                            <TextField
                                                key={index}
                                                id={`otp-${index}`}
                                                value={otpValues[index]}
                                                onChange={(e) =>
                                                    handleOtpChange(index, e.target.value)
                                                }
                                                onKeyDown={(e) => handleKeyDown(index, e)}
                                                inputProps={{
                                                    maxLength: 1,
                                                    inputMode: "numeric",
                                                    pattern: "[0-9]*",
                                                    style: {
                                                        textAlign: "center",
                                                        fontSize: "1.5rem",
                                                        padding: "8px",
                                                        width: "60px",
                                                        letterSpacing: "normal",
                                                    },
                                                }}
                                                sx={{
                                                    background: "white",
                                                    "& .MuiOutlinedInput-root": {
                                                        "& fieldset": {
                                                            borderColor: "#e0e0e0",
                                                        },
                                                        "&:hover fieldset": {
                                                            borderColor: "#80bdff",
                                                        },
                                                        "&.Mui-focused fieldset": {
                                                            borderColor: "#1976d2",
                                                        },
                                                    },
                                                }}
                                            />
                                        ))}
                                </Box>
                            </FormControl>
                        </VerificationBox>
                        {props.enableOrDisableMFA === "enable" ? (
                            <Typography variant="body1" sx={{ mt: 2 }}>
                                Please enter the 6-digit code from your authenticator app to enable MFA.
                            </Typography>
                        ) : (
                            <Typography variant="body1" sx={{ mt: 2 }}>
                                Please enter the 6-digit code from your authenticator app to disable MFA.
                            </Typography>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button variant="outlined" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => props.enableOrDisableMFA === "enable" ? enableMFA() : disableMFA()}
                    // disabled={otpValues.some((v) => v === "")} // Optional validation
                    >
                        Submit
                    </Button>
                </DialogActions>
            </Dialog>

        </>
    );
};
export default MFAPopup;